import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, MoreHorizontal } from 'lucide-react';
import { DiscountCodeZ as DiscountCode } from '@libs/schemas';
import { formatLongDate } from '@libs/services/date';
import { trpc } from 'src/trpc';
import { FC, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { useToast } from 'components/useToast';

const getStatusBadge = (code: DiscountCode) => {
  const isExpired = code.expiresAt && new Date(code.expiresAt) < new Date();

  if (code.archived) {
    return <Badge variant="secondary">Archived</Badge>;
  }

  if (isExpired) {
    return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Expired</Badge>;
  }

  if (code.active) {
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>;
  }

  return <Badge variant="secondary">Inactive</Badge>;
};

const DiscountCodeUsage: FC<{ code: DiscountCode }> = ({ code }) => {
  const usageCount = code.usageCount ?? 0;

  return (
    <>
      {code.usageLimit && (
        <div className="text-sm text-muted-foreground">
          {usageCount} / {code.usageLimit.toLocaleString()}
        </div>
      )}
      {!code.usageLimit && (
        <div className="text-sm text-muted-foreground">{usageCount} / Unlimited</div>
      )}
    </>
  );
};

// Separate component for actions to handle mutations
const DiscountCodeActions: FC<{
  code: DiscountCode;
  onEdit: (code: DiscountCode) => void;
}> = ({ code, onEdit }) => {
  const utils = trpc.useUtils();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const { successToast, errorToast } = useToast();

  const deleteMutation = trpc.admin.discountCodes.deleteDiscount.useMutation({
    onSuccess: () => {
      successToast({ title: 'Discount code deleted successfully' });
      utils.admin.discountCodes.getAllDiscountCodes.invalidate();
    },
    onError: (error) => {
      errorToast({ title: 'Failed to delete discount code', description: error.message });
    },
  });

  const handleDelete = () => {
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    await deleteMutation.mutateAsync(code.code);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(code)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleDelete}
            disabled={deleteMutation.isPending || code.archived}
            className="text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={openDeleteDialog}
        onOpenChange={setOpenDeleteDialog}
        title="Delete Discount Code"
        description={`Are you sure you want to delete the discount code "${code.code}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={confirmDelete}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export const createDiscountCodeColumns = (
  onEdit: (code: DiscountCode) => void,
): ColumnDef<DiscountCode>[] => [
  {
    accessorKey: 'code',
    header: 'Discount Code',
    cell: ({ row }) => {
      return <div className="font-medium text-gray-900">{row.original.code}</div>;
    },
  },
  {
    accessorKey: 'active',
    header: 'Status',
    cell: ({ row }) => getStatusBadge(row.original),
  },
  {
    id: 'usage',
    header: 'Usage',
    cell: ({ row }) => <DiscountCodeUsage code={row.original} />,
  },
  {
    accessorKey: 'expiresAt',
    header: 'Expires',
    cell: ({ row }) => {
      const expiresAt = row.original.expiresAt;
      return expiresAt ? (
        <div className="text-sm">{formatLongDate(new Date(expiresAt))}</div>
      ) : (
        <span className="text-sm text-muted-foreground">No expiry</span>
      );
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => <DiscountCodeActions code={row.original} onEdit={onEdit} />,
  },
];
